/**
 * 喜用神 / 忌神计算引擎
 * 基于日主强弱 + 格局 + 五行平衡综合判断
 * 算法：
 *   1. 计算日主旺衰（得令、得势、得地综合评分）
 *   2. 身强→用克泄耗之神；身弱→用生扶之神
 *   3. 结合格局给出具体喜用神建议
 * 遵循"温和表述"，提供调和建议
 */

import {
  type Stem, type Element, type FourPillars,
  STEMS, STEM_ELEMENT, BRANCH_HIDDEN,
  calcWuxingScores,
} from '../../baziCalc';
import { getShishen, ELEMENT_COLORS, type Shishen } from './shishenEngine';
import { type GejuResult } from './gejuEngine';
import {
  ELEMENT_GENERATES,
  ELEMENT_OVERCOMES,
  ELEMENT_OVERCOME_BY,
  ELEMENT_GENERATED_BY,
  WANGXIANG_TABLE,
  STEM_YIN_YANG,
} from '@/utils/calcTools/unifiedRules';

// ── 喜用神条目 ─────────────────────────────────────────────
export interface XiyongItem {
  element: Element;
  reason: string;
  color: string;
  icon: string;
}

// ── 学理口径条目 ─────────────────────────────────────────
export interface XiyongApproach {
  key: 'fuyi' | 'tiaohou' | 'bingyao';
  label: string;
  desc: string;
  xi: XiyongItem[];
  ji: XiyongItem[];
  summary: string;
}

export interface XiyongResult {
  xi: XiyongItem[];
  ji: XiyongItem[];
  tips: Array<{ icon: string; title: string; value: string; color: string }>;
  advice: string;
  bodyStrength: 'strong' | 'weak' | 'neutral';
  dayStem: Stem;
  dayStemNick: { yinyang: string; nature: string };
  monthBranch: string;
  monthQiDesc: string;
  approaches: XiyongApproach[];
}

// ── 五行对应图标 ────────────────────────────────────────────
const ELEMENT_ICONS: Record<Element, string> = {
  木: 'ri-plant-line',
  火: 'ri-fire-line',
  土: 'ri-landscape-line',
  金: 'ri-medal-line',
  水: 'ri-drop-line',
};

// ── 幸运颜色/数字/方位查表 ────────────────────────────────
const ELEMENT_LUCKY_COLOR: Record<Element, string> = {
  木: '绿色、青色',
  火: '红色、橙色、紫色',
  土: '黄色、棕色、米色',
  金: '白色、银色、金色',
  水: '黑色、蓝色、深灰',
};
const ELEMENT_LUCKY_NUMBER: Record<Element, string> = {
  木: '3、8（木数）',
  火: '2、7（火数）',
  土: '5、0（土数）',
  金: '4、9（金数）',
  水: '1、6（水数）',
};
const ELEMENT_LUCKY_DIRECTION: Record<Element, string> = {
  木: '东方、东南方',
  火: '南方',
  土: '中央、西南、东北',
  金: '西方、西北方',
  水: '北方',
};
const ELEMENT_LUCKY_PLACE: Record<Element, string> = {
  木: '山林地带、东方城市',
  火: '阳光充足的南方城市',
  土: '平原、内陆城市',
  金: '西方城市、商业发达地区',
  水: '近水之地、北方或滨海城市',
};

// ── 天干意象（喜用神个性化文案用）──────────────────────────
const STEM_NICKNAMES: Record<Stem, { yinyang: string; nature: string }> = {
  甲: { yinyang: '阳木', nature: '参天大树，挺拔向上，根基深厚，如栋梁之材' },
  乙: { yinyang: '阴木', nature: '藤蔓花草，柔韧灵动，善于借势攀援，如兰花之姿' },
  丙: { yinyang: '阳火', nature: '太阳之光，热烈明亮，普照万物，具有领袖气质' },
  丁: { yinyang: '阴火', nature: '灯烛之火，温暖内敛，照亮幽微，如星光之辉' },
  戊: { yinyang: '阳土', nature: '高原厚土，稳重厚实，承载万物，如城墙之固' },
  己: { yinyang: '阴土', nature: '田园之土，细腻温润，孕育生机，如沃壤之肥' },
  庚: { yinyang: '阳金', nature: '刀剑之金，刚健锐利，决断果敢，如斧钺之锋' },
  辛: { yinyang: '阴金', nature: '珠玉之金，精致温润，内敛光华，如首饰之美' },
  壬: { yinyang: '阳水', nature: '江河之水，奔腾浩荡，气势磅礴，如大海之广' },
  癸: { yinyang: '阴水', nature: '雨露之水，润泽细腻，渗透万物，如甘泉之清' },
};

// ── 月令旺衰描述（喜用）────────────────────────────────────
const XI_MONTH_QI_DESC: Record<string, string> = {
  旺: '月令当令，得时而旺，力量最为强劲，助力事半功倍',
  相: '月令得气，虽不主令，力量犹存，仍可发挥助力',
  休: '月令休囚，不得其时，力量平常，需配合其他因素',
  囚: '月令受囚，力量受制，此时助力有限，需借力调候',
  死: '月令死绝，力量最弱，虽有帮扶之名，实则杯水车薪',
};

// ── 月令旺衰描述（忌神）────────────────────────────────────
const JI_MONTH_QI_DESC: Record<string, string> = {
  旺: '月令当令，得时而旺，力量最为强劲，对日主不利加倍',
  相: '月令得气，虽不主令，力量犹存，仍有一定影响',
  休: '月令休囚，不得其时，力量平常，影响相对有限',
  囚: '月令受囚，力量受制，此时影响不大，但仍需留意',
  死: '月令死绝，力量最弱，虽有忌神之名，实则危害甚微',
};

// ── 月令旺相休囚死（统一使用 unifiedRules 中的 WANGXIANG_TABLE）──────────────────

/**
 * 获取五行在月令的旺相强度（0-4），归一化为系数（0.0-1.0）
 */
function getMonthQiCoeff(element: string, monthBranch: string): number {
  const strength = WANGXIANG_TABLE[element as Element]?.[monthBranch] ?? 2;
  return strength / 4;
}

/**
 * 计算日主旺衰
 * 得令（月令旺相休囚死加权）+ 得势（天干扶助）+ 得地（日支藏干）综合评判
 * 与 gejuEngine 的 WANGXIANG_TABLE 保持一致
 * 返回 'strong' | 'neutral' | 'weak'
 */
export function calcDayStrength(fourPillars: FourPillars): 'strong' | 'neutral' | 'weak' {
  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const monthBranch = fourPillars.month.branch;

  let score = 0;

  // ── 得令（月令旺相休囚死 × 藏干权重）──────────────────────
  // 月令是最重要的得气来源，用旺相系数加权
  const monthHidden = BRANCH_HIDDEN[monthBranch];
  for (const h of monthHidden) {
    const hEl = STEM_ELEMENT[h.stem];
    if (hEl === dayStemEl) {
      // 比肩/劫财 → 得令，乘以月令旺相系数
      const coeff = getMonthQiCoeff(dayStemEl, monthBranch);
      score += 3 * h.weight * (0.5 + coeff * 0.5); // 系数范围 0.5-1.0，避免死月完全归零
    } else if (ELEMENT_GENERATES[hEl] === dayStemEl) {
      // 印星生日主 → 半得令，同样乘以印星五行的月令旺相系数
      const coeff = getMonthQiCoeff(hEl, monthBranch);
      score += 2 * h.weight * (0.5 + coeff * 0.5);
    }
  }

  // ── 得势（年月时天干是否扶日主）──────────────────────────
  const otherStems = [fourPillars.year.stem, fourPillars.month.stem, fourPillars.hour.stem];
  for (const s of otherStems) {
    const el = STEM_ELEMENT[s];
    if (el === dayStemEl) score += 1.5; // 比劫
    else if (ELEMENT_GENERATES[el] === dayStemEl) score += 1; // 印星
  }

  // 拷贝一份供后面扣分使用
  const otherStemsForDeduction = otherStems;

  // ── 得地（年支、日支、时支藏干是否助日主）────────────────
  // 月支已在"得令"维度中计算，此处不再重复，避免双重加分
  const rootBranches = [fourPillars.year.branch, fourPillars.day.branch, fourPillars.hour.branch];
  for (const b of rootBranches) {
    for (const h of BRANCH_HIDDEN[b]) {
      const hEl = STEM_ELEMENT[h.stem];
      if (hEl === dayStemEl) {
        // 日支权重最高(2)，年支/时支次之(1)
        const coeff = b === fourPillars.day.branch ? 2 : 1;
        score += coeff * h.weight;
      } else if (ELEMENT_GENERATES[hEl] === dayStemEl) {
        // 日支权重(1)，年支/时支次之(0.5)
        const coeff = b === fourPillars.day.branch ? 1 : 0.5;
        score += coeff * h.weight;
      }
    }
  }

  // ── 克制扣分 ────────────────────────────────────────────
  // 地支藏干扣分
  const allBranches = [
    fourPillars.year.branch, fourPillars.month.branch,
    fourPillars.day.branch, fourPillars.hour.branch,
  ];
  for (const b of allBranches) {
    for (const h of BRANCH_HIDDEN[b]) {
      const hEl = STEM_ELEMENT[h.stem];
      if (ELEMENT_OVERCOMES[hEl] === dayStemEl) score -= 1.5 * h.weight; // 官杀克身
      else if (ELEMENT_GENERATES[dayStemEl] === hEl) score -= 0.5 * h.weight; // 食伤泄气
    }
  }
  // 天干扣分（排除日主本身，年/月/时天干均可克泄日主）
  for (const s of otherStemsForDeduction) {
    const sEl = STEM_ELEMENT[s];
    if (ELEMENT_OVERCOMES[sEl] === dayStemEl) score -= 1.2; // 官杀克身
    else if (ELEMENT_GENERATES[dayStemEl] === sEl) score -= 0.4; // 食伤泄气
  }

  if (score >= 5) return 'strong';
  if (score <= 2) return 'weak';
  return 'neutral';
}

/**
 * 计算喜用神和忌神
 * 整合扶抑、调候、病药三套学理口径
 */
export function calcXiyong(fourPillars: FourPillars, geju?: GejuResult): XiyongResult {
  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const monthBranch = fourPillars.month.branch;
  const bodyStrength = calcDayStrength(fourPillars);
  const wuxingScores = calcWuxingScores(fourPillars);

  // ── 1. 扶抑用神（主口径，向后兼容）─────────────────────
  const fuyi = calcFuyi(fourPillars, geju);

  // 构建 xi items（扶抑）
  const xi: XiyongItem[] = fuyi.xi.map(el => ({
    element: el,
    reason: buildXiReason(el, dayStem, monthBranch, bodyStrength),
    color: ELEMENT_COLORS[el],
    icon: ELEMENT_ICONS[el],
  }));

  // 构建 ji items（扶抑）
  const ji: XiyongItem[] = fuyi.ji.map(el => ({
    element: el,
    reason: buildJiReason(el, dayStem, monthBranch, bodyStrength),
    color: ELEMENT_COLORS[el],
    icon: ELEMENT_ICONS[el],
  }));

  // 开运建议提示（基于扶抑主口径）
  const tips = buildTips(fuyi.xi);
  const advice = buildAdvice(fuyi.xi, fuyi.ji, dayStemEl, bodyStrength);

  // 月令旺衰综合描述（基于扶抑主口径）
  const primaryXiEl = fuyi.xi[0];
  const monthQiForPrimary = primaryXiEl ? (WANGXIANG_TABLE[primaryXiEl]?.[monthBranch] ?? 2) : 2;
  const qiLabels = ['死', '囚', '休', '相', '旺'];
  const monthQiLabel = qiLabels[monthQiForPrimary];
  const monthQiDescMap: Record<string, string> = {
    旺: `月令${monthBranch}当令，喜用神得时而旺，此月令对命局最为有利，喜用神力量强劲`,
    相: `月令${monthBranch}得气，喜用神虽不主令，力量犹存，仍可发挥助力`,
    休: `月令${monthBranch}休囚，喜用神不得其时，力量平常，需借助其他五行配合`,
    囚: `月令${monthBranch}受囚，喜用神力量受制，此时需借力调候，方能发挥作用`,
    死: `月令${monthBranch}死绝，喜用神力量最弱，需通过后天调候来弥补先天不足`,
  };
  const monthQiDesc = monthQiDescMap[monthQiLabel] ?? `月令${monthBranch}，命局五行各有其位`;

  // ── 2. 调候用神 ────────────────────────────────────────
  const tiaohou = calcTiaohou(fourPillars);

  // ── 3. 病药用神 ────────────────────────────────────────
  const bingyao = calcBingyao(fourPillars);

  // ── 4. 组装三种口径 ────────────────────────────────────
  const approaches: XiyongApproach[] = [
    {
      key: 'fuyi',
      label: '扶抑用神',
      desc: '以日主强弱为核心：身强宜泄耗，身弱宜生扶。这是最常用的推断方法。',
      xi: elementsToItems(fuyi.xi, dayStem, monthBranch, bodyStrength),
      ji: elementsToJiItems(fuyi.ji, dayStem, monthBranch, bodyStrength),
      summary: fuyi.summary,
    },
    {
      key: 'tiaohou',
      label: '调候用神',
      desc: '以月令寒热燥湿为核心：夏月喜水调候，冬月喜火取暖，春秋视燥湿而定。',
      xi: elementsToItems(tiaohou.xi, dayStem, monthBranch, bodyStrength),
      ji: elementsToJiItems(tiaohou.ji, dayStem, monthBranch, bodyStrength),
      summary: tiaohou.summary,
    },
    {
      key: 'bingyao',
      label: '病药思路',
      desc: '以五行失衡为病，找克制过旺、生扶不足之药。重在纠偏补缺。',
      xi: elementsToItems(bingyao.xi, dayStem, monthBranch, bodyStrength),
      ji: elementsToJiItems(bingyao.ji, dayStem, monthBranch, bodyStrength),
      summary: bingyao.summary,
    },
  ];

  return {
    xi, ji, tips, advice,
    bodyStrength,
    dayStem,
    dayStemNick: STEM_NICKNAMES[dayStem],
    monthBranch,
    monthQiDesc,
    approaches,
  };
}

function adjustByGeju(
  geju: GejuResult,
  xiElements: Element[],
  jiElements: Element[],
  dayStemEl: Element,
  bodyStrength: 'strong' | 'weak' | 'neutral',
): void {
  // 七杀格且身强：以食伤制杀为喜，印不可过多
  if (geju.name === '七杀格' && bodyStrength !== 'weak') {
    const foodEl = ELEMENT_GENERATES[dayStemEl];
    if (!xiElements.includes(foodEl)) xiElements.push(foodEl);
  }
  // 伤官格：伤官格见官非吉，需印绶为喜
  if (geju.name === '伤官格') {
    const yinEl = ELEMENT_GENERATED_BY[dayStemEl];
    if (!xiElements.includes(yinEl)) xiElements.push(yinEl);
  }
  // 格局自带喜用
  for (const el of geju.lucky) {
    if (!xiElements.includes(el)) xiElements.push(el);
  }
  for (const el of geju.unlucky) {
    if (!jiElements.includes(el)) jiElements.push(el);
  }
}

function buildXiReason(el: Element, dayStem: Stem, monthBranch: string, strength: 'strong' | 'weak' | 'neutral'): string {
  const dayStemEl = STEM_ELEMENT[dayStem];
  const dayYY = STEM_YIN_YANG[dayStem];
  const nick = STEM_NICKNAMES[dayStem];
  const monthQi = WANGXIANG_TABLE[el]?.[monthBranch] ?? 2;
  const qiLabels = ['死', '囚', '休', '相', '旺'];
  const qiLabel = qiLabels[monthQi];
  const qiDesc = XI_MONTH_QI_DESC[qiLabel] ?? '';

  const parts: string[] = [];
  parts.push(`日主${dayStem}为${nick.yinyang}，${nick.nature}`);

  if (el === dayStemEl) {
    parts.push(`。${el}为同类比劫，${qiDesc}。`);
    parts.push(`身弱见比劫如${dayYY === 'yang' ? '大树成林、相互扶持' : '花草成簇、聚势而生'}，帮扶有力，根基更稳。`);
  } else if (ELEMENT_GENERATED_BY[dayStemEl] === el) {
    parts.push(`。${el}为印星，生扶日主，${qiDesc}。`);
    parts.push(`如${dayYY === 'yang' ? '江河润木、源泉不竭' : '雨露滋苗、润物无声'}，使日主有力，命格更稳固。`);
  } else if (ELEMENT_GENERATES[dayStemEl] === el) {
    parts.push(`。${el}为食伤，泄秀日主，${qiDesc}。`);
    parts.push(`将旺盛能量化为才华与表达，${dayYY === 'yang' ? '如烈日生光、光芒万丈' : '如烛火传辉、温暖照人'}，格局流畅。`);
  } else if (ELEMENT_OVERCOME_BY[dayStemEl] === el) {
    parts.push(`。${el}为官杀，制衡日主，${qiDesc}。`);
    parts.push(`使旺盛能量有所节制，${dayYY === 'yang' ? '如斧斤修枝、成栋梁之材' : '如雕琢美玉、成器用之物'}，反成格局之用。`);
  } else if (ELEMENT_OVERCOMES[dayStemEl] === el) {
    parts.push(`。${el}为财星，日主身旺可用财，${qiDesc}。`);
    parts.push(`财运因此稳健，${dayYY === 'yang' ? '如大树结果、收获丰盈' : '如藤蔓结实、渐次有成'}。`);
  } else {
    parts.push(`。此五行有助于命局平衡，使命局更加和谐。`);
  }

  return parts.join('');
}

function buildJiReason(el: Element, dayStem: Stem, monthBranch: string, bodyStrength: 'strong' | 'weak' | 'neutral'): string {
  const dayStemEl = STEM_ELEMENT[dayStem];
  const dayYY = STEM_YIN_YANG[dayStem];
  const nick = STEM_NICKNAMES[dayStem];
  const monthQi = WANGXIANG_TABLE[el]?.[monthBranch] ?? 2;
  const qiLabels = ['死', '囚', '休', '相', '旺'];
  const qiLabel = qiLabels[monthQi];
  const qiDesc = JI_MONTH_QI_DESC[qiLabel] ?? '';

  const parts: string[] = [];
  parts.push(`【命理提示】日主${dayStem}为${nick.yinyang}，${nick.nature}`);

  if (el === dayStemEl) {
    parts.push(`。当前${el}为同类比劫，${qiDesc}。`);
    parts.push(`日主已旺再加比劫则过强，旺极则反为不利，${dayYY === 'yang' ? '如密林无隙、阳光难入' : '如藤蔓过盛、主枝受损'}，需适度疏剪。`);
    parts.push(`【现实表现】此阶段容易竞争加剧、人际摩擦增多，或精力过于分散而难以聚焦核心目标。`);
    parts.push(`【调和方法】
① 明确合作边界，重要事务书面确认；
② 优先推进1-2个核心项目，减少并行任务；
③ 通过运动、写作等方式疏导过剩精力；
④ 多接触克制${el}的五行环境以平衡能量。`);
    parts.push(`【鼓励收束】命理显示这一阶段需要"收"而非"放"，适度收敛反而能让您的核心优势更加突出。每一次对精力的聚焦，都是在为下一阶段蓄力。`);
  } else if (ELEMENT_GENERATED_BY[dayStemEl] === el) {
    parts.push(`。${el}为印星，生扶日主，${qiDesc}。`);
    parts.push(`日主已强再加印则旺极失衡，${dayYY === 'yang' ? '如大水淹木、根烂于泥' : '如雨露过多、花谢于湿'}，适当减少为佳。`);
    parts.push(`【现实表现】此阶段容易思虑过多、行动偏慢，或因过度依赖经验而错失新机会，亦可能长辈介入过多影响独立判断。`);
    parts.push(`【调和方法】
① 设定明确的deadline，以时间约束推动行动；
② 每周尝试一件全新的小事，打破思维惯性；
③ 在重大决策中主动征求年轻一代或跨行业朋友的意见；
④ 减少静态独处时间，增加轻度社交与户外活动。`);
    parts.push(`【鼓励收束】思虑是您的优势，但"做"才能让想法落地生根。这一阶段适合把已有的积累转化为具体产出，让智慧真正变现。`);
  } else if (ELEMENT_GENERATES[dayStemEl] === el) {
    parts.push(`。${el}为食伤，泄秀日主，${qiDesc}。`);
    parts.push(`身弱时耗损日主能量，使弱身更弱，${dayYY === 'yang' ? '如烈火焚柴、薪尽火灭' : '如灯油耗尽、光渐暗淡'}，需注意保护。`);
    parts.push(`【现实表现】此阶段才华表达欲望强但精力可能跟不上，容易陷入"想法多、执行少"的循环，或因过度输出而感到身心俱疲。`);
    parts.push(`【调和方法】
① 采用"最小可行输出"策略，先完成再完美；
② 严格保护睡眠与休息时间，创作不宜透支健康；
③ 将创意想法记录存档，待精力充沛时集中执行；
④ 多接触生扶日主的五行元素以补充能量。`);
    parts.push(`【鼓励收束】才华需要节奏感来承载。暂时放缓输出速度，不是放弃创作，而是为了让下一波灵感来得更有力量。您的才华值得被更好地保存和释放。`);
  } else if (ELEMENT_OVERCOME_BY[dayStemEl] === el) {
    parts.push(`。${el}为官杀，克制日主，${qiDesc}。`);
    parts.push(`身弱再受克制则压力过重，${dayYY === 'yang' ? '如斧斤伐幼苗、栋梁未成先折' : '如严霜压嫩蕊、芳华易损'}，建议回避。`);
    parts.push(`【现实表现】此阶段外界压力较大，可能面临上级严格要求、竞争加剧或突发责任增加，容易感到被环境推着走、自主空间受限。`);
    parts.push(`【调和方法】
① 将大目标拆解为周计划，降低心理压力阈值；
② 主动向上级或合作方沟通资源需求，争取缓冲空间；
③ 通过印星（学习、进修）或比劫（团队、伙伴）通关调和；
④ 每日预留15分钟独处冥想，重建内心秩序。`);
    parts.push(`【鼓励收束】压力是成长的催化剂，前提是您给自己留足恢复的空间。命理显示这一阶段是"锻造期"而非"衰退期"，适度承压后您的抗压能力会显著提升。`);
  } else if (ELEMENT_OVERCOMES[dayStemEl] === el) {
    parts.push(`。${el}为财星，消耗日主，${qiDesc}。`);
    parts.push(`弱身无力养财，财来财去，${dayYY === 'yang' ? '如大树未壮先结果、枝枯果落' : '如幼苗未长先开花、养分耗尽'}，需先固本培元。`);
    parts.push(`【现实表现】此阶段财务波动可能加大，或出现"机会多但精力不足"的情况，容易因过度追逐收益而透支根基，投资需格外审慎。`);
    parts.push(`【调和方法】
① 优先稳固主业现金流，减少高风险投入；
② 建立"防守型"财务缓冲（预留3-6个月应急资金）；
③ 重大财务决策延后至精力充沛时段；
④ 将注意力从"获取更多"转向"守住已有"。`);
    parts.push(`【鼓励收束】财务节奏的调整是智慧而非退缩。命理提示您：先把根扎深，果实自然会来。短期的稳健，是为了中长期的丰盛。`);
  } else {
    parts.push(`。此五行对命局平衡有一定干扰，建议减少接触。`);
    parts.push(`【现实表现】生活中可能遇到与该五行相关的小波折，需留意相关领域的变化信号。`);
    parts.push(`【调和方法】适度减少该五行对应的环境、颜色与行为的接触频率，同时加强喜用神五行的补充。`);
    parts.push(`【鼓励收束】命理只是提示一个阶段的能量倾向，您的主动调整永远是最好的"优化"。保持觉察、适度应对，即可平稳度过。`);
  }

  return parts.join('');
}

function buildTips(xiElements: Element[]): Array<{ icon: string; title: string; value: string; color: string }> {
  const tips: Array<{ icon: string; title: string; value: string; color: string }> = [];
  const primaryEl = xiElements[0];
  const secondaryEl = xiElements[1];

  if (primaryEl) {
    tips.push({
      icon: 'ri-palette-line',
      title: '幸运颜色',
      value: xiElements.map(e => ELEMENT_LUCKY_COLOR[e]).join('、'),
      color: ELEMENT_COLORS[primaryEl],
    });
    tips.push({
      icon: 'ri-compass-3-line',
      title: '吉利方位',
      value: xiElements.map(e => ELEMENT_LUCKY_DIRECTION[e]).join('、'),
      color: ELEMENT_COLORS[primaryEl],
    });
    tips.push({
      icon: 'ri-number-1',
      title: '幸运数字',
      value: xiElements.map(e => ELEMENT_LUCKY_NUMBER[e]).join('、'),
      color: secondaryEl ? ELEMENT_COLORS[secondaryEl] : ELEMENT_COLORS[primaryEl],
    });
    tips.push({
      icon: 'ri-map-pin-line',
      title: '适宜居住',
      value: ELEMENT_LUCKY_PLACE[primaryEl],
      color: secondaryEl ? ELEMENT_COLORS[secondaryEl] : ELEMENT_COLORS[primaryEl],
    });
  }
  return tips;
}

function buildAdvice(
  xiElements: Element[],
  jiElements: Element[],
  dayStemEl: Element,
  bodyStrength: 'strong' | 'weak' | 'neutral',
): string {
  const xiColors = xiElements.map(e => ELEMENT_LUCKY_COLOR[e]).join('、');
  const jiColors = jiElements.map(e => ELEMENT_LUCKY_COLOR[e]).join('、');
  const xiDirs = xiElements.map(e => ELEMENT_LUCKY_DIRECTION[e]).join('、');
  const strengthDesc = bodyStrength === 'strong' ? '日主旺盛' : bodyStrength === 'weak' ? '日主偏弱' : '日主中和';

  const isWeakWithWealth = bodyStrength === 'weak' && jiElements.includes(ELEMENT_OVERCOMES[dayStemEl] as Element);
  const isBijieJi = jiElements.includes(dayStemEl);
  const isQishaJi = jiElements.includes(ELEMENT_OVERCOME_BY[dayStemEl] as Element);

  const parts: string[] = [];
  parts.push(`命局${strengthDesc}，`);

  if (bodyStrength === 'weak') {
    parts.push(`这不是坏命，而是提醒您这一阶段更适合稳住节奏、借力使力。`);
    if (isWeakWithWealth) {
      parts.push(`身弱财旺时，先稳现金流，减少高杠杆和人情借贷；以印星（学习进修）和比劫（团队合作）为助力，待根基稳固后再图大财。`);
    }
  } else if (bodyStrength === 'strong') {
    parts.push(`身强能量充沛，关键在于找到合适的「泄口」，让旺盛的能量转化为实际成就。`);
    if (isBijieJi) {
      parts.push(`比劫为忌时，合作宜先定规则，避免利益模糊；重要项目的权责需书面明确，防止争财内耗。`);
    }
  } else {
    parts.push(`中和之格最宜保持动态平衡，旺年进取、衰年守成，顺势而为即可。`);
  }

  if (isQishaJi) {
    parts.push(`官杀为忌时，降低冒进节奏，建立流程和边界；遇到权威压制时，用成果和证据表达而非硬刚对抗。`);
  }

  parts.push(`日常宜多接触${xiColors}系颜色，朝向${xiDirs}出行或布置工作空间。${jiColors ? `适当减少${jiColors}系颜色接触，以保持命局平衡。` : ''}只要提前做好风险管理，反而能把压力转成成长机会。命理是了解自己特质的工具，积极调整环境与心态，方能让每一分能量都得到最好的发挥。`);

  return parts.join('');
}

// ── 调候用神：月令寒热判断 ────────────────────────────────
/** 调候用神：根据月令寒热燥湿判断需要调候的五行 */
function calcTiaohou(fourPillars: FourPillars): { xi: Element[]; ji: Element[]; summary: string } {
  const monthBranch = fourPillars.month.branch;
  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];

  // 月令寒热分类
  const HOT_BRANCHES = ['巳', '午', '未'];
  const COLD_BRANCHES = ['亥', '子', '丑'];
  const DAMP_BRANCHES = ['寅', '卯', '辰'];
  const DRY_BRANCHES = ['申', '酉', '戌'];

  const xiElements: Element[] = [];
  const jiElements: Element[] = [];
  let summary = '';

  if (HOT_BRANCHES.includes(monthBranch)) {
    // 夏月炎热，需水调候降温，金生水助
    xiElements.push('水');
    if (dayStemEl !== '金' && dayStemEl !== '水') xiElements.push('金');
    jiElements.push('火');
    if (dayStemEl !== '木') jiElements.push('木');
    summary = `生于${monthBranch}月，夏令炎热，火气当权，命局偏燥。调候用神首选「水」以降温润燥，次取「金」以生水助调候。忌神为「火」助热、「木」生火，愈增燥热。调候之理，在于平衡寒热，使命局趋于温润。`;
  } else if (COLD_BRANCHES.includes(monthBranch)) {
    // 冬月寒冷，需火调候取暖，木生火助
    xiElements.push('火');
    if (dayStemEl !== '木' && dayStemEl !== '火') xiElements.push('木');
    jiElements.push('水');
    if (dayStemEl !== '金') jiElements.push('金');
    summary = `生于${monthBranch}月，冬令寒冷，水气当权，命局偏寒。调候用神首选「火」以取暖驱寒，次取「木」以生火助调候。忌神为「水」助寒、「金」生水，愈增阴冷。调候之理，在于温养命根，使生机得以萌发。`;
  } else if (DAMP_BRANCHES.includes(monthBranch)) {
    // 春月湿重，需火/土调候燥湿
    xiElements.push('火');
    if (dayStemEl !== '土') xiElements.push('土');
    jiElements.push('水');
    if (dayStemEl !== '木') jiElements.push('木');
    summary = `生于${monthBranch}月，春令湿重，木气旺盛，命局偏湿。调候用神首选「火」以燥湿温暖，次取「土」以吸收湿气。忌神为「水」增湿、「木」吸水，愈增阴湿。调候之理，在于燥湿相济，使土气得以发挥作用。`;
  } else if (DRY_BRANCHES.includes(monthBranch)) {
    // 秋月燥金，需水润燥，金气已旺无需再助
    xiElements.push('水');
    if (dayStemEl !== '金' && dayStemEl !== '水') xiElements.push('金');
    jiElements.push('火');
    if (dayStemEl !== '土') jiElements.push('土');
    summary = `生于${monthBranch}月，秋令燥金，金气肃杀，命局偏燥。调候用神首选「水」以润燥养木，次取「金」以生水助调候。忌神为「火」助燥、「土」生金过旺，愈增干燥。调候之理，在于润燥相济，使木气得以涵养。`;
  } else {
    // 默认不应到达这里
    xiElements.push('水');
    jiElements.push('火');
    summary = `生于${monthBranch}月，调候用神以平衡命局寒热为主。`;
  }

  return { xi: [...new Set(xiElements)], ji: [...new Set(jiElements)], summary };
}

// ── 病药用神：基于五行失衡找病配药 ──────────────────────
/** 病药用神：五行最旺者为病（过旺），克制它的为药（喜）；最弱者为病（不足），生扶它的为药 */
function calcBingyao(fourPillars: FourPillars): { xi: Element[]; ji: Element[]; summary: string } {
  const wuxingScores = calcWuxingScores(fourPillars);
  const sorted = [...wuxingScores].sort((a, b) => b.percent - a.percent);
  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];
  const dayStemEl = STEM_ELEMENT[fourPillars.day.stem];

  const xiElements: Element[] = [];
  const jiElements: Element[] = [];

  // 病一：过旺的五行 → 药：克制它的五行
  const excessCurer = ELEMENT_OVERCOME_BY[strongest.element];
  xiElements.push(excessCurer);
  // 同时泄耗过旺五行
  const excessDrainer = ELEMENT_GENERATES[strongest.element];
  if (excessDrainer && !xiElements.includes(excessDrainer)) {
    xiElements.push(excessDrainer);
  }
  // 忌：过旺五行的同类（再增旺）和生它的（助旺）
  if (strongest.element !== dayStemEl) {
    jiElements.push(strongest.element);
  }
  const excessGenerator = ELEMENT_GENERATED_BY[strongest.element];
  if (excessGenerator && excessGenerator !== dayStemEl && !jiElements.includes(excessGenerator)) {
    jiElements.push(excessGenerator);
  }

  // 病二：最弱的五行 → 药：生扶它的五行
  const deficiencyCurer = ELEMENT_GENERATED_BY[weakest.element];
  if (deficiencyCurer && !xiElements.includes(deficiencyCurer)) {
    xiElements.push(deficiencyCurer);
  }
  // 同类帮扶
  if (weakest.element !== dayStemEl && !xiElements.includes(weakest.element)) {
    xiElements.push(weakest.element);
  }
  // 忌：克制最弱五行的（再削弱）和它所生的（泄耗）
  const deficiencyKiller = ELEMENT_OVERCOME_BY[weakest.element];
  if (deficiencyKiller && !jiElements.includes(deficiencyKiller)) {
    jiElements.push(deficiencyKiller);
  }
  const deficiencyDrainer = ELEMENT_GENERATES[weakest.element];
  if (deficiencyDrainer && !jiElements.includes(deficiencyDrainer)) {
    jiElements.push(deficiencyDrainer);
  }

  // 去重与过滤
  const xiUniq = [...new Set(xiElements)].filter(e => !jiElements.includes(e) && e !== dayStemEl);
  const jiUniq = [...new Set(jiElements)].filter(e => !xiElements.includes(e));

  // 兜底：确保至少有一个喜用和一个忌神
  const finalXi = xiUniq.length > 0 ? xiUniq : [excessCurer];
  const finalJi = jiUniq.length > 0 ? jiUniq : [strongest.element !== dayStemEl ? strongest.element : (ELEMENT_GENERATES[strongest.element] ?? '火')];

  const summary = `病药思路分析：命局中「${strongest.element}」气最旺（${strongest.percent.toFixed(1)}%），为病一——过旺则需制，以「${ELEMENT_OVERCOME_BY[strongest.element]}」克制之；「${weakest.element}」气最弱（${weakest.percent.toFixed(1)}%），为病二——不足则需补，以「${ELEMENT_GENERATED_BY[weakest.element] ?? weakest.element}」生扶之。药随病转，对症下方，方能平衡。`;

  return { xi: finalXi, ji: finalJi, summary };
}

// ── 扶抑用神：基于日主强弱 ────────────────────────────────
/** 扶抑用神：现有核心逻辑，保持向后兼容 */
function calcFuyi(
  fourPillars: FourPillars,
  geju?: GejuResult,
): { xi: Element[]; ji: Element[]; summary: string; bodyStrength: 'strong' | 'weak' | 'neutral' } {
  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const bodyStrength = calcDayStrength(fourPillars);
  const wuxingScores = calcWuxingScores(fourPillars);

  const xiElements: Element[] = [];
  const jiElements: Element[] = [];

  if (bodyStrength === 'strong') {
    xiElements.push(ELEMENT_OVERCOME_BY[dayStemEl]);
    xiElements.push(ELEMENT_GENERATES[dayStemEl]);
    xiElements.push(ELEMENT_OVERCOMES[dayStemEl]);
    jiElements.push(dayStemEl);
    jiElements.push(ELEMENT_GENERATED_BY[dayStemEl]);
  } else if (bodyStrength === 'weak') {
    xiElements.push(dayStemEl);
    xiElements.push(ELEMENT_GENERATED_BY[dayStemEl]);
    jiElements.push(ELEMENT_OVERCOME_BY[dayStemEl]);
    jiElements.push(ELEMENT_GENERATES[dayStemEl]);
    jiElements.push(ELEMENT_OVERCOMES[dayStemEl]);
  } else {
    const sorted = [...wuxingScores].sort((a, b) => b.percent - a.percent);
    const strongest = sorted[0];
    const weakest = sorted[sorted.length - 1];
    if (strongest.element !== dayStemEl) {
      xiElements.push(ELEMENT_OVERCOME_BY[strongest.element]);
    }
    if (!xiElements.includes(weakest.element)) {
      xiElements.push(weakest.element);
    }
    jiElements.push(strongest.element);
  }

  if (geju) {
    adjustByGeju(geju, xiElements, jiElements, dayStemEl, bodyStrength);
  }

  const xiUniq = [...new Set(xiElements)].filter(e => !jiElements.includes(e));
  const jiUniq = [...new Set(jiElements)].filter(e => !xiElements.includes(e));

  // 兜底：确保至少有一个喜用和一个忌神
  const finalXi = xiUniq.length > 0 ? xiUniq : [dayStemEl];
  const finalJi = jiUniq.length > 0 ? jiUniq : [ELEMENT_OVERCOME_BY[dayStemEl] ?? '火'];

  const strengthDesc = bodyStrength === 'strong' ? '身强' : bodyStrength === 'weak' ? '身弱' : '中和';
  const summary = `扶抑用神法：日主${dayStem}属${dayStemEl}，经综合评判为「${strengthDesc}」。${bodyStrength === 'strong' ? '身强宜泄耗，以官杀、食伤、财星为喜用。' : bodyStrength === 'weak' ? '身弱宜生扶，以比劫、印星为喜用。' : '日主中和，以补弱泻旺为调候方向。'}此为核心推断方法，占主导地位。`;

  return { xi: finalXi, ji: finalJi, summary, bodyStrength };
}

// ── 辅助：将元素数组转为 XiyongItem 数组 ─────────────────
function elementsToItems(
  elements: Element[],
  dayStem: Stem,
  monthBranch: string,
  strength: 'strong' | 'weak' | 'neutral',
): XiyongItem[] {
  if (elements.length === 0) return [];
  return elements.map(el => ({
    element: el,
    reason: buildXiReason(el, dayStem, monthBranch, strength),
    color: ELEMENT_COLORS[el],
    icon: ELEMENT_ICONS[el],
  }));
}

function elementsToJiItems(
  elements: Element[],
  dayStem: Stem,
  monthBranch: string,
  strength: 'strong' | 'weak' | 'neutral',
): XiyongItem[] {
  if (elements.length === 0) return [];
  return elements.map(el => ({
    element: el,
    reason: buildJiReason(el, dayStem, monthBranch, strength),
    color: ELEMENT_COLORS[el],
    icon: ELEMENT_ICONS[el],
  }));
}